import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { AlertTriangle, CloudOff, ExternalLink } from 'lucide-react';

/**
 * SpotBlockedBanner — "why isn't spot launching?" insight strip.
 *
 * Reads GET /v1/clusters/{id}/spot/blockers (aggregated NodeClaim launch
 * failures the agent observed: quota / ICE / other) and, when there is
 * evidence in the window, renders a single warning banner above the savings
 * hero. Without this the dashboard reads healthily "right-sized" while every
 * spot launch silently fails on the account's spot vCPU quota (live-hit on
 * eks-dev 2026-07-10) — the one state an operator must never be blind to.
 *
 * Renders NOTHING when there are no blockers (blocked=false) or on any fetch
 * error — the banner only ever adds signal, never noise.
 */
const KIND_LABEL = {
  quota: 'Spot quota exhausted',
  ice: 'AWS spot capacity unavailable',
  other: 'Spot launches failing',
};

const SpotBlockedBanner = ({ clusterId }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!clusterId) return;
    try {
      const res = await axios.get(`${API}/v1/clusters/${clusterId}/spot/blockers`);
      setData(res.data);
    } catch {
      setData(null); // no evidence ⇒ no banner; never block the page on this
    }
  }, [clusterId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 120000); // blockers age out; refresh lazily
    return () => clearInterval(t);
  }, [load]);

  if (!data?.blocked) return null;

  const worst = data.blockers?.[0] || {};
  const isQuota = worst.kind === 'quota';
  const Icon = isQuota ? AlertTriangle : CloudOff;
  const failures = (data.blockers || []).reduce((n, b) => n + (b.count || 0), 0);

  return (
    <div
      data-testid="spot-blocked-banner"
      className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 px-4 py-3"
    >
      <Icon className="h-5 w-5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">
          {KIND_LABEL[worst.kind] || KIND_LABEL.other}
          <span className="ml-2 font-normal text-amber-700 dark:text-amber-300">
            {failures} failed launch{failures === 1 ? '' : 'es'} in the last {data.window_hours}h
          </span>
        </div>
        <p className="text-sm text-amber-800 dark:text-amber-300 mt-0.5">{data.headline}</p>
        {worst.last_reason && (
          <p className="text-xs font-mono text-amber-700/80 dark:text-amber-400/80 mt-1 truncate">
            {worst.last_reason}
          </p>
        )}
        {isQuota && (
          <a
            href="https://console.aws.amazon.com/servicequotas/home/services/ec2/quotas/L-34B43A08"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 dark:text-amber-200 underline mt-1"
          >
            Request a spot vCPU quota increase <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
};

export default SpotBlockedBanner;
